import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gift, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import './RedeemPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClaimPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, login-required, processing, error, success
  const [error, setError] = useState('');
  const [membershipName, setMembershipName] = useState('');

  const checkAndClaim = useCallback(async () => {
    const token = localStorage.getItem('hyperdriveToken');
    console.log('ClaimPage - Token exists:', !!token, 'Code:', code);
    
    if (!token) {
      console.log('ClaimPage - No token, showing login required');
      setStatus('login-required');
      return;
    }

    // User is logged in - claim the membership
    setStatus('processing');
    console.log('ClaimPage - Processing claim for code:', code);
    
    try {
      const response = await fetch(`${API_URL}/api/membership/claim/${code}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('ClaimPage - Response status:', response.status);
      const data = await response.json();
      console.log('ClaimPage - Response data:', data);
      
      if (response.ok && data.checkout_url) {
        setMembershipName(data.membership);
        setStatus('success');
        console.log('ClaimPage - Redirecting to Stripe:', data.checkout_url);
        // Small delay to show success status, then redirect to Stripe
        setTimeout(() => {
          window.location.href = data.checkout_url;
        }, 500);
      } else if (response.status === 401) {
        // Token expired or invalid - clear it and show login
        console.log('ClaimPage - Token invalid, clearing and showing login');
        localStorage.removeItem('hyperdriveToken');
        localStorage.removeItem('hyperdriveTokenExpiry');
        setStatus('login-required');
      } else {
        setError(data.detail || 'Failed to claim membership');
        setStatus('error');
      }
    } catch (err) {
      console.error('ClaimPage - Error:', err);
      setError('Something went wrong. Please try again.');
      setStatus('error');
    }
  }, [code]);

  useEffect(() => {
    // Small delay to ensure token is saved after registration redirect
    const timer = setTimeout(() => {
      checkAndClaim();
    }, 100);
    return () => clearTimeout(timer);
  }, [checkAndClaim]);

  const handleLogin = () => {
    // After login, redirect back here
    navigate(`/login`, { state: { returnTo: `/claim/${code}` } });
  };

  const handleRegister = () => {
    navigate(`/login`, { state: { tab: 'register', returnTo: `/claim/${code}` } });
  };

  return (
    <div className="redeem-page">
      <div className="redeem-container">
        <div className="redeem-header">
          <div className="redeem-icon">
            <Gift size={48} />
          </div>
          <h1>Claim Your Membership</h1>
          <p>Code: <strong>{code}</strong></p>
        </div>

        {status === 'checking' && (
          <div className="redeem-status">
            <Loader2 size={40} className="spin" />
            <p>Checking your account...</p>
          </div>
        )}

        {status === 'login-required' && (
          <div className="redeem-status">
            <h2>Please Login or Register First</h2>
            <p>You need an account to claim your membership.</p>
            <div className="claim-buttons">
              <button onClick={handleLogin} className="claim-btn primary">
                Login
              </button>
              <button onClick={handleRegister} className="claim-btn secondary">
                Create Account
              </button>
            </div>
          </div>
        )}

        {status === 'processing' && (
          <div className="redeem-status">
            <Loader2 size={40} className="spin" />
            <p>Setting up your {membershipName || 'membership'}...</p>
            <p className="subtext">Redirecting to secure checkout...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="redeem-status">
            <CheckCircle size={40} className="success-icon" />
            <h2>Success!</h2>
            <p>Redirecting to Stripe checkout...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="redeem-status">
            <AlertCircle size={40} className="error-icon" />
            <h2>Oops!</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/redeem')} className="claim-btn primary">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimPage;
